import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { clientPhotosActions } from '../../_actions/clientPhotos.actions';
import ClientPhotoLightbox from '../clients/ClientPhotoLightbox';
import ClientPhotoConsentModal from '../clients/ClientPhotoConsentModal';

const STRIP_LIMIT_ALL = 5;

const CameraGlyph = () => (
    <svg width="28" height="28" viewBox="0 0 64 64" fill="none">
        <rect x="6" y="16" width="52" height="38" rx="6" stroke="#29282D" strokeWidth="2" />
        <path d="M22 16l3-6h14l3 6" stroke="#29282D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="32" cy="35" r="10" stroke="#29282D" strokeWidth="2" />
        <circle cx="32" cy="35" r="4" fill="#007EA0" />
    </svg>
);

/**
 * Inline photos strip inside the booking detail panel's Client tab.
 *
 * Renders two labelled strips:
 *   1. "This booking" — photos linked to the current bookingId. Only shown
 *      when there is at least one, so the most contextually relevant shots
 *      sit top-of-column during a live session.
 *   2. "All photos" — the most recent photos across the whole client
 *      history, deduped against strip 1. Capped at STRIP_LIMIT_ALL visible
 *      tiles so the "+N" overflow stays within the slide-out width on
 *      mobile. Tapping the overflow or the "See all →" link closes the
 *      booking slide-out and navigates to the full client profile with
 *      the Photos tab pre-selected.
 *
 * Uploads always pre-link to the current booking so new shots land in
 * strip 1 automatically.
 */
const BookingPhotosInline = ({ clientId, clientName, bookingId, onSeeAll }) => {
    const dispatch = useDispatch();
    const state = useSelector((s) => s.clientPhotos?.byClientId?.[clientId] || null);
    const fileInputRef = useRef(null);
    const [lightbox, setLightbox] = useState(null); // { items, index }
    const [consentModalPending, setConsentModalPending] = useState(null);
    const undoTimeoutsRef = useRef({});

    const items = state?.items || [];
    const pendingUploads = state?.pendingUploads || [];
    const hasConsent = !!state?.consent?.givenAt;
    const loading = state?.loading;

    useEffect(() => {
        if (!clientId) return;
        dispatch(clientPhotosActions.fetchPhotos(clientId));
        dispatch(clientPhotosActions.fetchSalonTags());
    }, [clientId, dispatch]);

    const { thisBookingItems, otherItems } = useMemo(() => {
        const matching = [];
        const rest = [];
        items.forEach((p) => {
            if (bookingId && p.bookingId === bookingId) {
                matching.push(p);
            } else {
                rest.push(p);
            }
        });
        return { thisBookingItems: matching, otherItems: rest };
    }, [items, bookingId]);

    const runUploads = useCallback((files) => {
        Array.from(files).forEach((file) => {
            dispatch(clientPhotosActions.uploadPhoto(clientId, file, { bookingId }));
        });
    }, [clientId, bookingId, dispatch]);

    const handleAddClick = () => {
        if (!hasConsent) {
            setConsentModalPending('pick');
            return;
        }
        if (fileInputRef.current) fileInputRef.current.click();
    };

    const handleFilesPicked = (e) => {
        const files = e.target.files;
        if (!files || !files.length) return;
        runUploads(files);
        e.target.value = '';
    };

    const handleConsentConfirmed = async () => {
        await dispatch(clientPhotosActions.setConsent(clientId));
        const action = consentModalPending;
        setConsentModalPending(null);
        if (action === 'pick') {
            setTimeout(() => {
                if (fileInputRef.current) fileInputRef.current.click();
            }, 50);
        }
    };

    const scheduleDeleteCommit = (photoId) => {
        const timer = setTimeout(() => {
            dispatch(clientPhotosActions.commitDelete(clientId, photoId));
            delete undoTimeoutsRef.current[photoId];
        }, 5000);
        undoTimeoutsRef.current[photoId] = timer;
    };

    // Leaving mid-undo still deletes: commit pending deletes instead of
    // dropping them (a cleared timer left the photo on the server).
    useEffect(() => {
        const flush = () => {
            Object.entries(undoTimeoutsRef.current).forEach(([photoId, timer]) => {
                clearTimeout(timer);
                dispatch(clientPhotosActions.commitDelete(clientId, photoId));
            });
            undoTimeoutsRef.current = {};
        };
        window.addEventListener('pagehide', flush);
        return () => { window.removeEventListener('pagehide', flush); flush(); };
    }, [clientId, dispatch]);

    const handleDeleteFromLightbox = (photoId) => {
        dispatch(clientPhotosActions.deletePhoto(clientId, photoId));
        scheduleDeleteCommit(photoId);
    };

    const handleRetag = (photoId, tags, previousTags) => {
        dispatch(clientPhotosActions.patchPhotoTags(clientId, photoId, tags, previousTags));
    };

    // "View booking" from inside the inline strip: we're already on
    // the diary, so just close the lightbox and fire the universal
    // beu-open-booking event DiaryV2 listens for. DiaryV2 will update
    // selectedEvent and swap the open BookingDetailPanel to the new
    // booking.
    const handleViewBooking = (photo) => {
        if (!photo?.bookingId) return;
        setLightbox(null);
        setTimeout(() => {
            document.dispatchEvent(new CustomEvent('beu-open-booking', {
                detail: { bookingId: photo.bookingId, date: photo.bookingDate },
            }));
        }, 180);
    };

    // Ask the parent booking panel to open a nested ClientDetailPanel
    // with the Photos tab pre-selected — same pattern as the existing
    // "View full client profile" button. The booking panel stays mounted
    // behind so back navigation returns straight to it.
    const openFullClientPhotos = useCallback(() => {
        if (typeof onSeeAll === 'function') onSeeAll();
    }, [onSeeAll]);

    const totalCount = items.length;
    const hasAny = totalCount > 0 || pendingUploads.length > 0;
    const isEmpty = !loading && !hasAny;

    // "All photos" strip is the most recent four non-this-booking items.
    const allStripVisible = otherItems.slice(0, STRIP_LIMIT_ALL);
    const allStripHidden = Math.max(0, otherItems.length - STRIP_LIMIT_ALL);

    const openLightboxAt = (sourceItems, index) => {
        setLightbox({ items: sourceItems, index });
    };

    return (
        <div className="bdp-section">
            <div className="bdp-section-header-row">
                <h4 className="bdp-section-title">Photos</h4>
                <button type="button" className="bdp-note-edit-btn" onClick={handleAddClick}>
                    Add
                </button>
            </div>

            {loading && !hasAny && (
                <div className="bdp-photos-strip bdp-photos-strip--loading">
                    {[0, 1, 2].map((i) => (
                        <div key={i} className="bdp-photo-skel" />
                    ))}
                </div>
            )}

            {isEmpty && (
                <button type="button" className="bdp-photos-empty-lux" onClick={handleAddClick}>
                    <span className="bdp-photos-empty-art" aria-hidden><CameraGlyph /></span>
                    <span className="bdp-photos-empty-copy">
                        <span className="bdp-photos-empty-title">
                            No photos for {clientName?.split(' ')[0] || 'this client'} yet
                        </span>
                        <span className="bdp-photos-empty-sub">Capture a shot to start the record.</span>
                    </span>
                </button>
            )}

            {pendingUploads.length > 0 && (
                <div className="bdp-photos-group">
                    <div className="bdp-photos-strip">
                        {pendingUploads.map((p) => (
                            <div key={p.tempId} className={`bdp-photo-tile bdp-photo-tile--pending ${p.error ? 'bdp-photo-tile--failed' : ''}`}>
                                <img src={p.thumbUrl} alt="" />
                                {!p.error && <div className="bdp-photo-spinner" />}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {thisBookingItems.length > 0 && (
                <div className="bdp-photos-group">
                    <div className="bdp-photos-group-header">
                        <span className="bdp-photos-group-label">This booking</span>
                        <span className="bdp-photos-group-count">{thisBookingItems.length}</span>
                    </div>
                    <div className="bdp-photos-strip">
                        {thisBookingItems.slice(0, 5).map((photo, idx) => (
                            <button
                                key={photo.id}
                                type="button"
                                className="bdp-photo-tile"
                                onClick={() => openLightboxAt(thisBookingItems, idx)}
                                aria-label="View photo"
                            >
                                <img src={photo.thumbUrl || photo.url} alt="" loading="lazy" />
                            </button>
                        ))}
                        {thisBookingItems.length > 5 && (
                            <button
                                type="button"
                                className="bdp-photo-tile bdp-photo-tile--more"
                                onClick={() => openLightboxAt(thisBookingItems, 5)}
                                aria-label={`View ${thisBookingItems.length - 5} more`}
                            >
                                +{thisBookingItems.length - 5}
                            </button>
                        )}
                    </div>
                </div>
            )}

            {otherItems.length > 0 && (
                <div className="bdp-photos-group">
                    <div className="bdp-photos-group-header">
                        <span className="bdp-photos-group-label">All photos</span>
                        <span className="bdp-photos-group-count">{totalCount}</span>
                    </div>
                    <div className="bdp-photos-strip">
                        {allStripVisible.map((photo, idx) => (
                            <button
                                key={photo.id}
                                type="button"
                                className="bdp-photo-tile"
                                onClick={() => openLightboxAt(otherItems, idx)}
                                aria-label="View photo"
                            >
                                <img src={photo.thumbUrl || photo.url} alt="" loading="lazy" />
                            </button>
                        ))}
                        {allStripHidden > 0 && (
                            <button
                                type="button"
                                className="bdp-photo-tile bdp-photo-tile--more"
                                onClick={openFullClientPhotos}
                                aria-label={`See all ${totalCount} photos`}
                            >
                                +{allStripHidden}
                            </button>
                        )}
                    </div>
                </div>
            )}

            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                capture="environment"
                style={{ display: 'none' }}
                onChange={handleFilesPicked}
            />

            {lightbox && (
                <ClientPhotoLightbox
                    items={lightbox.items}
                    initialIndex={lightbox.index}
                    onClose={() => setLightbox(null)}
                    onDelete={(photoId) => {
                        handleDeleteFromLightbox(photoId);
                        setLightbox(null);
                    }}
                    onRetag={handleRetag}
                    onViewBooking={handleViewBooking}
                />
            )}

            {consentModalPending && (
                <ClientPhotoConsentModal
                    clientName={clientName}
                    onConfirm={handleConsentConfirmed}
                    onCancel={() => setConsentModalPending(null)}
                />
            )}
        </div>
    );
};

export default BookingPhotosInline;
